// src/components/comments/CommentList.tsx
import React, { useCallback } from 'react';
import { type RecipeComment } from '../../api/Types';
import { useRecipeComments } from '../../domains/comments';
import CommentItem from './CommentItem';
import AddCommentForm from './AddCommentForm';
import { Alert, Box, Heading, ListGroup, Spinner, Text } from '../ui';

interface CommentListProps {
  recipeId: string;
  className?: string; // Add className prop
}

const CommentList: React.FC<CommentListProps> = ({ recipeId, className }) => {
  const { comments, loading, error, setComments } = useRecipeComments(recipeId);

  const handleCommentUpdated = useCallback((updatedComment: RecipeComment) => {
    setComments((currentComments) =>
      currentComments.map((comment) =>
        comment.id === updatedComment.id ? updatedComment : comment
      )
    );
  }, [setComments]);

  const handleCommentDeleted = useCallback((commentId: number) => {
    setComments((currentComments) =>
      currentComments.filter((comment) => comment.id !== commentId)
    );
  }, [setComments]);

  if (loading) {
    return <Spinner label="Chargement des commentaires..." />;
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <Box data-testid="comment-list-root" marginTop={5} className={className}>
      <Heading as="h4" style={{ margin: '1.5rem' }}>Commentaires</Heading>
      <ListGroup>
        {comments.length > 0 ? (
          <ListGroup>
            {comments.map(comment => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onCommentUpdated={handleCommentUpdated}
                onCommentDeleted={handleCommentDeleted}
              />
            ))}
          </ListGroup>
        ) : (
          <Text padding={3}>Aucun commentaire pour le moment.</Text>
        )}
      </ListGroup>
      <AddCommentForm recipeId={recipeId} />
    </Box>
  );
};

export default CommentList;
